import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Loader2, Crown, Download, Share2, Link2, RefreshCw, Eye, Plus, Trash2, ArrowUp, ArrowDown, ExternalLink, Lock, FileText, Sparkles } from "lucide-react";
import { generateMediaKitPdf } from "@/lib/mediaKitPdf";
import { MEDIA_KIT_THEMES, THEME_KEYS, getTheme } from "@/lib/mediaKitThemes";
import { MediaKitPreview } from "@/components/mediakit/MediaKitPreview";
import { PerformanceReportsComingSoon } from "@/components/mediakit/PerformanceReportsComingSoon";

const SHOW_FIELDS = [
  ["photo", "Profile photo"], ["bio", "Bio / about"], ["location", "Location"],
  ["niches", "Niches"], ["platforms", "Platforms"], ["socials", "Social handles"],
  ["social_stats", "Follower / performance stats"], ["portfolio", "Portfolio"],
  ["services", "Services"], ["rates", "Rates"], ["collaborations", "Previous collaborations"],
  ["testimonials", "Testimonials"], ["manual_stats", "Extra highlights (creator-provided)"],
];

function buildPreview(settings, source) {
  const show = settings.show || {};
  const d = { display_name: source.display_name, tagline: settings.tagline, theme: settings.theme || "emerald", powered_by: "Nearfluence", niches: show.niches ? source.niches : [] };
  if (show.photo) d.profile_photo = source.profile_photo;
  if (show.location) { d.location = source.location; d.areas_covered = source.areas_covered; }
  if (show.bio) d.bio = source.bio;
  if (show.socials) {
    d.socials = {};
    Object.entries(source.socials || {}).forEach(([k, v]) => {
      const e = { username: v.username, url: v.url, source: v.source, last_updated: v.last_updated };
      if (show.social_stats) { ["followers", "avg_views", "engagement"].forEach((m) => { if (v[m]) e[m] = v[m]; }); }
      d.socials[k] = e;
    });
    d.platforms = show.platforms ? Object.keys(source.socials || {}) : [];
  } else if (show.platforms) d.platforms = Object.keys(source.socials || {});
  if (show.portfolio) d.portfolio = source.portfolio;
  if (show.services) { d.services = source.services; d.gifted_ok = source.gifted_ok; }
  if (show.rates) { d.rates_text = settings.rates_text && !settings.rates_on_request ? settings.rates_text : ""; d.rates_on_request = !!(settings.rates_on_request || !settings.rates_text); }
  if (show.collaborations) d.collaborations = settings.collaborations;
  if (show.testimonials) d.testimonials = settings.testimonials;
  if (show.manual_stats) d.manual_stats = settings.manual_stats;
  if (settings.show_contact_email && settings.contact_email) d.contact_email = settings.contact_email;
  return d;
}

const EXAMPLE = {
  display_name: "Sample Creator", tagline: "Local food & lifestyle creator", location: "Reading", theme: "berry",
  bio: "I create warm, authentic UGC for independent cafes and wellness brands — great even with a smaller, highly engaged audience.",
  niches: ["Food", "Lifestyle"], powered_by: "Nearfluence",
  socials: { instagram: { username: "samplecreator", followers: 4200, engagement: 6.1, source: "self_reported" } },
  platforms: ["instagram"], services: [{ type: "ugc", price_from: 80 }, { type: "posted", price_from: 120 }],
  collaborations: [{ brand: "The Ivy Cafe", description: "3 launch reels", date: "Mar 2025" }],
  rates_on_request: true, portfolio: [],
};

export default function MediaKit() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [shareBusy, setShareBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/creator/media-kit"); setData(r.data); setSettings(r.data.settings); setDirty(false); }
    catch (e) { if (e.response?.status === 404) setData({ needs_profile: true, is_pro: false }); else toast.error("Could not load your media kit"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const preview = useMemo(() => (settings && data?.source ? buildPreview(settings, data.source) : null), [settings, data]);
  const set = (patch) => { setSettings((s) => ({ ...s, ...patch })); setDirty(true); };
  const setShow = (k, v) => { setSettings((s) => ({ ...s, show: { ...s.show, [k]: v } })); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/creator/media-kit", {
        show: settings.show, tagline: settings.tagline, theme: settings.theme,
        rates_text: settings.rates_text,
        rates_on_request: settings.rates_on_request, manual_stats: settings.manual_stats,
        collaborations: settings.collaborations, testimonials: settings.testimonials,
        contact_email: settings.contact_email, show_contact_email: settings.show_contact_email,
      });
      setData(r.data); setSettings(r.data.settings); setDirty(false);
      toast.success("Media kit saved");
      return r.data;
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save"); return null; }
    finally { setSaving(false); }
  };

  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      let published = data.published;
      if (dirty) { const saved = await save(); if (!saved) { setPdfBusy(false); return; } published = saved.published; }
      await generateMediaKitPdf(published);
      toast.success("Media kit PDF downloaded");
    } catch (e) { toast.error("Could not generate the PDF"); }
    finally { setPdfBusy(false); }
  };

  const toggleShare = async (enabled) => {
    if (enabled && dirty) await save();
    setShareBusy(true);
    try {
      const r = await api.post("/creator/media-kit/share", { enabled });
      setData((d) => ({ ...d, settings: { ...d.settings, share_enabled: r.data.share_enabled, share_id: r.data.share_id }, share_paused_by_expiry: false }));
      setSettings((s) => ({ ...s, share_enabled: r.data.share_enabled, share_id: r.data.share_id }));
      toast.success(enabled ? "Sharing enabled" : "Sharing disabled — the public link no longer works");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update sharing"); }
    finally { setShareBusy(false); }
  };
  const regenerate = async () => {
    if (!window.confirm("Generate a new link? The current link will stop working immediately. Note: media kit PDFs already downloaded by others cannot be recalled.")) return;
    setShareBusy(true);
    try {
      const r = await api.post("/creator/media-kit/share/regenerate");
      setSettings((s) => ({ ...s, share_id: r.data.share_id, share_enabled: r.data.share_enabled }));
      setData((d) => ({ ...d, settings: { ...d.settings, share_id: r.data.share_id, share_enabled: r.data.share_enabled } }));
      toast.success("New link generated — the old one is now inactive");
    } catch { toast.error("Could not regenerate the link"); }
    finally { setShareBusy(false); }
  };

  const shareUrl = settings?.share_id ? `${window.location.origin}/m/${settings.share_id}` : "";
  const copyLink = () => { navigator.clipboard.writeText(shareUrl); toast.success("Link copied"); };
  const nativeShare = async () => {
    if (navigator.share) { try { await navigator.share({ title: "My Nearfluence Media Kit", url: shareUrl }); } catch { /* cancelled */ } }
    else copyLink();
  };

  if (loading) return <div className="flex min-h-screen items-center justify-center bg-[#FAF9F6]"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>;

  // No creator profile yet
  if (data?.needs_profile) return (
    <div className="mx-auto max-w-lg px-4 py-16 text-center" data-testid="media-kit-needs-profile">
      <h1 className="font-display text-2xl font-bold text-zinc-900">Build your profile first</h1>
      <p className="mt-2 text-zinc-500">Add your photo, bio and links, then your media kit fills in automatically.</p>
      <Button onClick={() => navigate("/creator/profile")} className="mt-5 rounded-full bg-emerald-600 hover:bg-emerald-700">Set up my profile</Button>
    </div>
  );

  const isPro = data?.is_pro;
  const proExpired = data?.pro_expired;

  // Free (never had Pro): explanation + example preview + upgrade
  if (!isPro && !proExpired) return (
    <div className="mx-auto max-w-5xl px-4 py-10 nf-pb-nav" data-testid="media-kit-locked">
      <div className="rounded-3xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-6 sm:p-8">
        <div className="inline-flex items-center gap-2 rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-800"><Crown className="h-3.5 w-3.5" /> Creator Pro</div>
        <h1 className="mt-3 font-display text-3xl font-bold text-zinc-900">Your professional Media Kit</h1>
        <p className="mt-2 max-w-2xl text-zinc-600">Turn your profile into a polished, shareable media kit — perfect for pitching local brands. Customise what appears, download a clean PDF with clickable links, and share a public link. Great for UGC creators, even with a smaller following.</p>
        <Button data-testid="media-kit-upgrade" onClick={() => navigate("/plans")} className="mt-5 rounded-full bg-emerald-600 hover:bg-emerald-700"><Crown className="mr-2 h-4 w-4" /> Upgrade to Creator Pro</Button>
      </div>
      <p className="mt-8 mb-3 text-center text-xs font-semibold uppercase tracking-wide text-zinc-400">Example preview — illustrative sample</p>
      <div className="pointer-events-none opacity-90"><MediaKitPreview kit={EXAMPLE} example /></div>
      <div className="mt-10"><PerformanceReportsComingSoon /></div>
    </div>
  );

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 nf-pb-nav" data-testid="media-kit-editor">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-zinc-900">Media Kit</h1>
          <p className="text-sm text-zinc-500">Customise, preview, download and share your media kit.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {!proExpired && <Button data-testid="media-kit-save" onClick={save} disabled={saving || !dirty} variant="outline" className="rounded-full">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>}
          <Button data-testid="media-kit-download" onClick={downloadPdf} disabled={pdfBusy || proExpired} className="rounded-full bg-emerald-600 hover:bg-emerald-700">{pdfBusy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Download className="mr-1 h-4 w-4" />} Download PDF</Button>
        </div>
      </div>

      {proExpired && (
        <div className="mt-4 rounded-2xl border border-amber-300 bg-amber-50 p-4" data-testid="media-kit-expired">
          <p className="flex items-center gap-2 font-semibold text-amber-900"><Lock className="h-4 w-4" /> Your Creator Pro access has ended</p>
          <p className="mt-1 text-sm text-amber-800">Your media kit is safely saved, but editing, new PDF exports and public sharing are paused. Public sharing has been switched off and won’t turn back on by itself — you’ll confirm it when you’re Pro again.</p>
          <Button onClick={() => navigate("/plans")} className="mt-3 rounded-full bg-emerald-600 hover:bg-emerald-700"><Crown className="mr-2 h-4 w-4" /> Restore Creator Pro</Button>
        </div>
      )}

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        {/* Editor */}
        <div className={`space-y-5 ${proExpired ? "pointer-events-none opacity-50" : ""}`} data-testid="media-kit-controls">
          {/* Sharing */}
          <section className="rounded-2xl border border-zinc-200 bg-white p-4">
            <h2 className="flex items-center gap-2 font-semibold text-zinc-800"><Share2 className="h-4 w-4 text-emerald-600" /> Public sharing</h2>
            <div className="mt-3 flex items-center justify-between">
              <span className="text-sm text-zinc-600">Enable a public link{settings.share_enabled ? "" : " (off by default)"}</span>
              <Switch data-testid="media-kit-share-toggle" checked={!!settings.share_enabled} onCheckedChange={toggleShare} disabled={shareBusy} />
            </div>
            {data.share_paused_by_expiry && !settings.share_enabled && <p className="mt-2 text-xs text-amber-700">Sharing was paused when your Pro access ended. Toggle it on to share again.</p>}
            {settings.share_enabled && (
              <div className="mt-3 space-y-2">
                <div className="flex gap-2">
                  <Input data-testid="media-kit-share-url" readOnly value={shareUrl} className="h-9 text-xs" />
                  <Button size="sm" data-testid="media-kit-copy" onClick={copyLink} variant="outline" className="h-9 rounded-full"><Link2 className="h-4 w-4" /></Button>
                  <Button size="sm" data-testid="media-kit-native-share" onClick={nativeShare} variant="outline" className="h-9 rounded-full"><Share2 className="h-4 w-4" /></Button>
                </div>
                <div className="flex gap-2">
                  <a href={shareUrl} target="_blank" rel="noreferrer"><Button size="sm" data-testid="media-kit-preview-public" variant="outline" className="h-8 rounded-full text-xs"><Eye className="mr-1 h-3.5 w-3.5" /> Preview public</Button></a>
                  <Button size="sm" data-testid="media-kit-regenerate" onClick={regenerate} variant="outline" className="h-8 rounded-full text-xs"><RefreshCw className="mr-1 h-3.5 w-3.5" /> Regenerate link</Button>
                </div>
                <p className="text-[11px] text-zinc-400">Public visitors see only what you’ve selected below. Email stays hidden unless you turn it on. Search engines are asked not to index it. Already-downloaded PDFs can’t be recalled.</p>
              </div>
            )}
          </section>

          {/* Theme / accent colour */}
          <section className="rounded-2xl border border-zinc-200 bg-white p-4" data-testid="theme-picker">
            <h2 className="font-semibold text-zinc-800">Theme colour</h2>
            <p className="mt-0.5 text-xs text-zinc-400">Pick an accent to match your brand. Applies to your preview, PDF and public page.</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {THEME_KEYS.map((k) => {
                const t = getTheme(k);
                const active = (settings.theme || "emerald") === k;
                return (
                  <button key={k} type="button" data-testid={`theme-${k}`} onClick={() => set({ theme: k })}
                    title={t.label}
                    className={`flex items-center gap-1.5 rounded-full border py-1 pl-1 pr-3 text-xs font-medium transition ${active ? "border-zinc-900 ring-2 ring-zinc-900/10" : "border-zinc-200 hover:border-zinc-300"}`}>
                    <span className="h-5 w-5 rounded-full" style={{ backgroundImage: `linear-gradient(135deg, ${t.gradFrom}, ${t.gradTo})` }} />
                    {t.label}
                  </button>
                );
              })}
            </div>
          </section>

          {/* What to show */}
          <section className="rounded-2xl border border-zinc-200 bg-white p-4">
            <h2 className="font-semibold text-zinc-800">What appears</h2>
            <div className="mt-3 grid grid-cols-2 gap-2">
              {SHOW_FIELDS.map(([k, label]) => (
                <label key={k} className="flex items-center gap-2 text-sm text-zinc-600">
                  <input type="checkbox" data-testid={`show-${k}`} checked={!!settings.show[k]} onChange={(e) => setShow(k, e.target.checked)} className="accent-emerald-600" />
                  {label}
                </label>
              ))}
            </div>
          </section>

          {/* Tagline + rates + contact */}
          <section className="rounded-2xl border border-zinc-200 bg-white p-4 space-y-3">
            <div><Label className="text-xs">Tagline</Label><Input data-testid="mk-tagline" value={settings.tagline} onChange={(e) => set({ tagline: e.target.value })} placeholder="e.g. Local food & lifestyle creator" className="mt-1 h-9" /></div>
            <div>
              <div className="flex items-center justify-between"><Label className="text-xs">Rates on request</Label><Switch data-testid="mk-rates-on-request" checked={!!settings.rates_on_request} onCheckedChange={(v) => set({ rates_on_request: v })} /></div>
              {!settings.rates_on_request && <Textarea data-testid="mk-rates-text" value={settings.rates_text} onChange={(e) => set({ rates_text: e.target.value })} rows={2} className="mt-2" placeholder="e.g. UGC from £80, in-feed post from £120" />}
            </div>
            <div>
              <Label className="text-xs">Contact email (optional)</Label>
              <Input data-testid="mk-contact-email" value={settings.contact_email} onChange={(e) => set({ contact_email: e.target.value })} placeholder={data.source.account_email} className="mt-1 h-9" />
              <label className="mt-1 flex items-center gap-2 text-xs text-zinc-500"><input type="checkbox" data-testid="mk-show-email" checked={!!settings.show_contact_email} onChange={(e) => set({ show_contact_email: e.target.checked })} className="accent-emerald-600" /> Show my email publicly (hidden by default)</label>
            </div>
          </section>

          {/* Previous collaborations */}
          <CollabEditor items={settings.collaborations} onChange={(v) => set({ collaborations: v })} />

          {/* Testimonials */}
          <ListEditor testid="testimonials" title="Testimonials" addLabel="Add testimonial"
            items={settings.testimonials} onChange={(v) => set({ testimonials: v })}
            fields={[["quote", "Quote", true, "textarea"], ["author", "Author"], ["role", "Role (e.g. Owner, Ivy Cafe)"]]} />

          {/* Manual highlights */}
          <ListEditor testid="manual-stats" title="Extra highlights (creator-provided)" addLabel="Add highlight"
            items={settings.manual_stats} onChange={(v) => set({ manual_stats: v })}
            fields={[["label", "Label (e.g. Avg reel views)", true], ["value", "Value (e.g. 12k)", true]]} />
        </div>

        {/* Live preview */}
        <div>
          <div className="sticky top-4">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-400">Live preview {dirty && <span className="ml-1 rounded-full bg-amber-100 px-2 py-0.5 text-amber-700">Unsaved — save before sharing/PDF</span>}</p>
            {preview && <MediaKitPreview kit={preview} />}
          </div>
        </div>
      </div>

      <div className="mt-10"><PerformanceReportsComingSoon /></div>
    </div>
  );
}

function newId() { return (window.crypto?.randomUUID?.() || `id-${Date.now()}-${Math.random()}`); }

function CollabEditor({ items = [], onChange }) {
  const add = () => onChange([...(items || []), { id: newId(), brand: "", description: "", date: "", url: "", image: "" }]);
  const upd = (i, patch) => onChange(items.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
  const del = (i) => onChange(items.filter((_, idx) => idx !== i));
  const move = (i, dir) => { const j = i + dir; if (j < 0 || j >= items.length) return; const a = [...items]; [a[i], a[j]] = [a[j], a[i]]; onChange(a); };
  const uploadImg = async (i, file) => {
    const fd = new FormData(); fd.append("file", file);
    try { const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); upd(i, { image: r.data.url }); } catch { toast.error("Image upload failed"); }
  };
  return (
    <section className="rounded-2xl border border-zinc-200 bg-white p-4" data-testid="collab-editor">
      <div className="flex items-center justify-between"><h2 className="font-semibold text-zinc-800">Previous collaborations</h2><Button size="sm" data-testid="collab-add" onClick={add} variant="outline" className="rounded-full"><Plus className="mr-1 h-4 w-4" /> Add</Button></div>
      <p className="mt-1 text-xs text-zinc-400">Shown as “Creator-provided”. Work done anywhere — not verified by Nearfluence.</p>
      <div className="mt-3 space-y-3">
        {(items || []).length === 0 && <p className="text-sm text-zinc-400">No collaborations added yet.</p>}
        {(items || []).map((c, i) => (
          <div key={c.id} data-testid={`collab-${i}`} className="rounded-xl border border-zinc-100 bg-zinc-50 p-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-zinc-500">#{i + 1}</span>
              <div className="flex gap-1">
                <button data-testid={`collab-up-${i}`} onClick={() => move(i, -1)} className="rounded p-1 text-zinc-500 hover:bg-zinc-200"><ArrowUp className="h-3.5 w-3.5" /></button>
                <button data-testid={`collab-down-${i}`} onClick={() => move(i, 1)} className="rounded p-1 text-zinc-500 hover:bg-zinc-200"><ArrowDown className="h-3.5 w-3.5" /></button>
                <button data-testid={`collab-del-${i}`} onClick={() => del(i)} className="rounded p-1 text-rose-500 hover:bg-rose-100"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
            <Input data-testid={`collab-brand-${i}`} value={c.brand} onChange={(e) => upd(i, { brand: e.target.value })} placeholder="Brand / business name *" className="mt-2 h-9" />
            <Textarea data-testid={`collab-desc-${i}`} value={c.description} onChange={(e) => upd(i, { description: e.target.value })} rows={2} placeholder="What you did *" className="mt-2" />
            <div className="mt-2 grid grid-cols-2 gap-2">
              <Input data-testid={`collab-date-${i}`} value={c.date} onChange={(e) => upd(i, { date: e.target.value })} placeholder="Month/year (optional)" className="h-9" />
              <Input data-testid={`collab-url-${i}`} value={c.url} onChange={(e) => upd(i, { url: e.target.value })} placeholder="Content URL (optional)" className="h-9" />
            </div>
            <div className="mt-2 flex items-center gap-2">
              {c.image ? <img src={c.image.startsWith("http") ? c.image : `${process.env.REACT_APP_BACKEND_URL}${c.image}`} alt="" className="h-10 w-10 rounded object-cover" /> : null}
              <label className="cursor-pointer text-xs text-emerald-700"><input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadImg(i, e.target.files[0])} />{c.image ? "Change image" : "Add image (optional)"}</label>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function ListEditor({ testid, title, addLabel, items = [], onChange, fields }) {
  const blank = () => { const o = { id: newId() }; fields.forEach(([k]) => (o[k] = "")); return o; };
  const add = () => onChange([...(items || []), blank()]);
  const upd = (i, k, v) => onChange(items.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  const del = (i) => onChange(items.filter((_, idx) => idx !== i));
  return (
    <section className="rounded-2xl border border-zinc-200 bg-white p-4" data-testid={`${testid}-editor`}>
      <div className="flex items-center justify-between"><h2 className="font-semibold text-zinc-800">{title}</h2><Button size="sm" data-testid={`${testid}-add`} onClick={add} variant="outline" className="rounded-full"><Plus className="mr-1 h-4 w-4" /> {addLabel}</Button></div>
      <div className="mt-3 space-y-3">
        {(items || []).length === 0 && <p className="text-sm text-zinc-400">Nothing added yet.</p>}
        {(items || []).map((it, i) => (
          <div key={it.id} data-testid={`${testid}-${i}`} className="rounded-xl border border-zinc-100 bg-zinc-50 p-3">
            <div className="flex justify-end"><button data-testid={`${testid}-del-${i}`} onClick={() => del(i)} className="rounded p-1 text-rose-500 hover:bg-rose-100"><Trash2 className="h-3.5 w-3.5" /></button></div>
            {fields.map(([k, ph, , type]) => type === "textarea"
              ? <Textarea key={k} data-testid={`${testid}-${k}-${i}`} value={it[k] || ""} onChange={(e) => upd(i, k, e.target.value)} rows={2} placeholder={ph} className="mt-1" />
              : <Input key={k} data-testid={`${testid}-${k}-${i}`} value={it[k] || ""} onChange={(e) => upd(i, k, e.target.value)} placeholder={ph} className="mt-1 h-9" />)}
          </div>
        ))}
      </div>
    </section>
  );
}
